"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import type { CommunityPost } from "@/lib/community/types"
import { usePost } from "@/lib/community/hooks/use-post"
import { loadPostDetail } from "@/lib/community/service/post-service"
import {
  togglePostLike,
  togglePostFavorite,
  copyAbilityToAccount,
  deployTeam,
} from "@/lib/community/service/interaction-service"
import { reportPostView } from "@/lib/community/community-api"
import { ApiError } from "@/lib/api/client"
import { useToast } from "@/lib/hooks/use-toast"
import { analytics } from "@/src/utils/analytics"

export interface PostDetailHandlers {
  handleLike: () => void
  handleFavorite: () => void
  handleCopyConfirm: () => void
  handleDeployConfirm: () => void
  handleEdit: () => void
}

export interface PanelStates {
  copyPanelOpen: boolean
  setCopyPanelOpen: (v: boolean) => void
  deployPanelOpen: boolean
  setDeployPanelOpen: (v: boolean) => void
  casePreviewOpen: boolean
  setCasePreviewOpen: (v: boolean) => void
  paymentPanelOpen: boolean
  setPaymentPanelOpen: (v: boolean) => void
  editModalOpen: boolean
  setEditModalOpen: (v: boolean) => void
  shareLinkOpen: boolean
  setShareLinkOpen: (v: boolean) => void
}

export interface UsePostDetailReturn {
  post: CommunityPost | null
  loading: boolean
  handlers: PostDetailHandlers
  panelStates: PanelStates
  refreshPost: () => Promise<void>
}

export function usePostDetail(postId: string): UsePostDetailReturn {
  const { post: storePost, loading } = usePost(postId)
  const [copyPanelOpen, setCopyPanelOpen] = useState(false)
  const [deployPanelOpen, setDeployPanelOpen] = useState(false)
  const [casePreviewOpen, setCasePreviewOpen] = useState(false)
  const [paymentPanelOpen, setPaymentPanelOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [shareLinkOpen, setShareLinkOpen] = useState(false)
  const toast = useToast()

  const pendingRef = useRef(new Set<string>())

  useEffect(() => {
    if (postId) reportPostView(postId)
  }, [postId])

  const refreshPost = useCallback(async () => {
    await loadPostDetail(postId, { force: true })
  }, [postId])

  const handleLike = useCallback(async () => {
    if (pendingRef.current.has("like")) return
    pendingRef.current.add("like")
    const isCurrentlyLiked = storePost?.isLiked ?? false
    try {
      await togglePostLike(postId)
      toast.success(isCurrentlyLiked ? "已取消点赞" : "点赞成功")
      analytics.track(isCurrentlyLiked ? "PostUnlike" : "PostLike", {
        post_id: postId,
        post_type: storePost?.abilityType ?? "",
      })
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("操作太频繁，请稍后再试")
      } else {
        toast.error("操作失败，请重试")
      }
    } finally {
      pendingRef.current.delete("like")
    }
  }, [postId, storePost, toast])

  const handleFavorite = useCallback(async () => {
    if (pendingRef.current.has("fav")) return
    pendingRef.current.add("fav")
    const isCurrentlyFavorited = storePost?.isFavorited ?? false
    try {
      await togglePostFavorite(postId)
      toast.success(isCurrentlyFavorited ? "已取消收藏" : "收藏成功")
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("操作太频繁，请稍后再试")
      } else {
        toast.error("操作失败，请重试")
      }
    } finally {
      pendingRef.current.delete("fav")
    }
  }, [postId, storePost, toast])

  const handleCopyConfirm = useCallback(async () => {
    if (pendingRef.current.has("copy")) return
    pendingRef.current.add("copy")
    try {
      await copyAbilityToAccount(postId)
      setCopyPanelOpen(false)
      toast.success("已复制到我的技能")
    } catch {
      toast.error("复制失败，请重试")
    } finally {
      pendingRef.current.delete("copy")
    }
  }, [postId, toast])

  const handleDeployConfirm = useCallback(async () => {
    if (pendingRef.current.has("deploy")) return
    pendingRef.current.add("deploy")
    try {
      await deployTeam(postId)
      setDeployPanelOpen(false)
      toast.success("部署成功")
    } catch {
      toast.error("部署失败，请重试")
    } finally {
      pendingRef.current.delete("deploy")
    }
  }, [postId, toast])

  const handleEdit = useCallback(() => {
    setEditModalOpen(true)
  }, [])

  return {
    post: storePost ?? null,
    loading,
    handlers: { handleLike, handleFavorite, handleCopyConfirm, handleDeployConfirm, handleEdit },
    panelStates: {
      copyPanelOpen, setCopyPanelOpen,
      deployPanelOpen, setDeployPanelOpen,
      casePreviewOpen, setCasePreviewOpen,
      paymentPanelOpen, setPaymentPanelOpen,
      editModalOpen, setEditModalOpen,
      shareLinkOpen, setShareLinkOpen,
    },
    refreshPost,
  }
}
